import { Message } from "@cohesive/api-client";
import { Entity, getEntityData } from "@cohesive/ui-components";
import { useMemo } from "react";

// Optimized entity deduplication using Map for O(1) lookups
// Returns a Map keyed by entity.data.id
const deduplicateEntities = (entities: Entity[]): Map<string, Entity> => {
  const entityMap = new Map<string, Entity>();

  for (const entity of entities) {
    const entityData = getEntityData(entity);
    if (entityData?.id) {
      const key = entityData.id.toString();
      // Keep the last occurrence of each entity (most recent data)
      entityMap.set(key, entity);
    }
  }

  return entityMap;
};

// Build a global entity store that accumulates all entities from the entire chat session
// Returns a single Map where keys are entity IDs and values are Entity objects
export const useGlobalEntityStore = (
  allMessages: Message[],
): Map<string, Entity> => {
  return useMemo(() => {
    const allEntities: Entity[] = [];

    for (const message of allMessages) {
      // Collect from user messages (like trigger payloads)
      if (message.origin === "user" && message.result?.entities) {
        const validEntities = message.result.entities.filter((entity) => {
          const entityData = getEntityData(entity);
          return entityData?.id;
        });
        allEntities.push(...validEntities);
      }

      // Collect from tool messages
      if (message.origin === "tool" && message.result?.entities) {
        const validEntities = message.result.entities.filter((entity) => {
          const entityData = getEntityData(entity);
          return entityData?.id;
        });
        allEntities.push(...validEntities);
      }
    }

    // Deduplicate - returns Map<string, Entity> keyed by entity.data.id
    return deduplicateEntities(allEntities);
  }, [allMessages]);
};
